import { useEffect, useState } from 'react';

/**
 * There's no centralized modal registry in this codebase — every modal/drawer
 * (`Dialog`, `ViewAppointmentDetails`, `CreateBookingModal`, settings
 * drawers, etc.) owns its own `isOpen` state and renders its own overlay.
 * What they DO have in common is the DOM shape: a `role="dialog"`/
 * `aria-modal="true"` panel, and/or a full-viewport `fixed inset-0` (or
 * `fixed inset-y-0`, for right-edge drawers) wrapper.
 *
 * This selector intentionally matches broadly on that shared shape rather
 * than requiring every modal to opt in — new modals get picked up for free
 * as long as they follow the existing overlay conventions.
 */
const MODAL_SELECTOR = '[role="dialog"], [aria-modal="true"], .fixed.inset-0, .fixed.inset-y-0';

/**
 * Elements (or descendants of elements) marked with this attribute are
 * ignored — used so Routy's own launcher/panel don't count as "a modal is
 * open" just because the expanded panel also happens to use `fixed inset-0`.
 */
const IGNORE_SELECTOR = '[data-routy-ui]';

function isElementVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') === 0) {
    return false;
  }
  // A click-through overlay isn't blocking anything, so it isn't "a modal".
  // Lots of persistent page scaffolding (drawer shells, map/layout wrappers,
  // toast containers) renders a full-screen `fixed inset-0` that stays
  // `pointer-events-none` until actually activated — counting those would
  // hide the Routy launcher on normal pages, which is the bug this guards.
  if (style.pointerEvents === 'none') {
    return false;
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Whether an element is genuinely acting as a modal/drawer right now, vs. just
 * being a full-screen layout/scaffolding wrapper that happens to use the same
 * `fixed inset-0` shape. Mirrors the heuristic `IntercomProvider` already uses
 * so the Routy launcher and the Intercom launcher hide/show in lockstep.
 */
function isActingAsModal(el: HTMLElement): boolean {
  if (!isElementVisible(el)) return false;

  // An explicit dialog role is an unambiguous signal on its own.
  if (el.getAttribute('role') === 'dialog' || el.getAttribute('aria-modal') === 'true') {
    return true;
  }

  // Otherwise it matched on `.fixed.inset-0` / `.fixed.inset-y-0`. Require a
  // real backdrop signal (a visible dimming/blur layer) so transparent
  // scaffolding wrappers with no backdrop don't count. `className` can be a
  // non-string (SVG) so guard the type.
  const classes = typeof el.className === 'string' ? el.className : '';
  if (classes.includes('bg-opacity-0')) return false;
  return (
    classes.includes('bg-opacity') ||
    classes.includes('bg-black') ||
    classes.includes('bg-ink') ||
    classes.includes('backdrop-blur') ||
    // A backdrop can also live as a direct child rather than on the wrapper
    // itself (e.g. ViewAppointmentDetails renders sibling backdrop + panel).
    !!el.querySelector(':scope > .bg-black, :scope > [class*="bg-ink-950"], :scope > [class*="backdrop-blur"]')
  );
}

function detectOpenModal(): boolean {
  const candidates = document.querySelectorAll<HTMLElement>(MODAL_SELECTOR);
  for (const el of candidates) {
    if (el.closest(IGNORE_SELECTOR)) continue;
    if (isActingAsModal(el)) return true;
  }
  return false;
}

/**
 * True while any modal/dialog/drawer overlay appears to be open anywhere in
 * the app (see `MODAL_SELECTOR` above for what counts). Used to hide global
 * floating UI — currently the Routy launcher — so it doesn't sit on top of
 * modal content.
 *
 * Implemented with a `MutationObserver` rather than per-modal registration
 * since modals here aren't wired into any shared context; mutations are
 * coalesced to one DOM check per animation frame to keep this cheap.
 */
export function useIsAnyModalOpen(): boolean {
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  useEffect(() => {
    let frame: number | null = null;

    const scheduleCheck = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setIsAnyModalOpen(detectOpenModal());
      });
    };

    scheduleCheck();

    const observer = new MutationObserver(scheduleCheck);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return isAnyModalOpen;
}
